/**
 * Color Override System
 *
 * Applies developer color overrides from localStorage by injecting a <style> tag.
 * Only used in dev/staging environments.
 */

const STORAGE_KEY = "markeroo-color-overrides";

export interface ColorOverrides {
  [cssVar: string]: string;
}

/**
 * Check if we're in a dev or staging environment.
 */
export function isDevOrStaging(): boolean {
  return import.meta.env.DEV || window.location.hostname === "markeroo.dev";
}

/**
 * Load color overrides from localStorage.
 */
export function loadColorOverrides(): ColorOverrides {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as ColorOverrides) : {};
  } catch {
    return {};
  }
}

/**
 * Save color overrides to localStorage.
 * Returns true if successful, false if quota exceeded.
 */
export function saveColorOverrides(overrides: ColorOverrides): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    return true;
  } catch (err) {
    if (err instanceof DOMException && err.name === "QuotaExceededError") {
      console.error("Failed to save color overrides: localStorage quota exceeded");
      return false;
    }
    throw err;
  }
}

/**
 * Validate a CSS variable name.
 * Must start with --color- to be a valid theme variable.
 */
function isValidCssVarName(name: string): boolean {
  return /^--color-[\w-]+$/.test(name);
}

/**
 * Validate a color value.
 * Accepts hex colors (#RGB, #RRGGBB) and rgb/rgba functions with valid ranges.
 */
function isValidColorValue(value: string): boolean {
  // Hex colors: #RGB or #RRGGBB
  if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value)) {
    return true;
  }

  // RGB: validate each component is 0-255
  const rgbMatch = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(value);
  if (rgbMatch) {
    const [, r, g, b] = rgbMatch.map(Number);
    return r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255;
  }

  // RGBA: validate RGB components 0-255, alpha 0-1
  const rgbaMatch = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/.exec(value);
  if (rgbaMatch) {
    const r = Number(rgbaMatch[1]);
    const g = Number(rgbaMatch[2]);
    const b = Number(rgbaMatch[3]);
    const a = parseFloat(rgbaMatch[4]);
    return (
      r >= 0 && r <= 255 &&
      g >= 0 && g <= 255 &&
      b >= 0 && b <= 255 &&
      !isNaN(a) && a >= 0 && a <= 1
    );
  }

  return false;
}

/**
 * Apply color overrides by injecting a <style> tag into the document head.
 * This is called on page load and whenever overrides change.
 *
 * Security: Validates CSS variable names and color values to prevent injection.
 */
export function applyColorOverrides(): void {
  const overrides = loadColorOverrides();
  const styleId = "markeroo-color-overrides-style";

  // Remove existing style tag if present
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }

  // If no overrides, nothing to inject
  if (Object.keys(overrides).length === 0) {
    return;
  }

  // Build CSS rules with validation
  const validRules: string[] = [];
  for (const [cssVar, value] of Object.entries(overrides)) {
    if (!isValidCssVarName(cssVar)) {
      console.warn(`Invalid CSS variable name ignored: ${cssVar}`);
      continue;
    }
    if (!isValidColorValue(value)) {
      console.warn(`Invalid color value ignored for ${cssVar}: ${value}`);
      continue;
    }
    validRules.push(`  ${cssVar}: ${value};`);
  }

  if (validRules.length === 0) {
    return;
  }

  const styleContent = `:root {\n${validRules.join("\n")}\n}`;

  // Inject new style tag
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = styleContent;
  document.head.appendChild(style);
}
